/**
 * PayHelm MCP Custom Tools — integrations entry point (single react-bootstrap Modal, multiple views).
 */

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Button } from 'react-bootstrap'
import {
  ChevronLeft,
  Wrench,
  Plus,
  FileDown,
  Loader2,
  Zap,
  Globe,
} from 'lucide-react'
import {
  fetchCustomTools,
  deleteCustomTool,
  updateCustomTool,
  testCustomTool,
  selectCustomTools,
  selectCustomToolsStatus,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import CustomToolForm from '../../AI/components/CustomToolForm'
import ImportSpecModal from '../../AI/components/ImportSpecModal'
import { CustomToolRowMenuPortal, CustomToolToggle } from '../../AI/components/CustomToolsListControls'
import { customToolHttpMethodColors } from '../../AI/components/customToolShared'

type View = 'list' | 'form' | 'import'

export interface CustomToolsIntegrationsModalProps {
  show: boolean
  onHide: () => void
}

const CustomToolsIntegrationsModal: React.FC<CustomToolsIntegrationsModalProps> = ({ show, onHide }) => {
  const dispatch = useDispatch()
  const items = useSelector(selectCustomTools)
  const status = useSelector(selectCustomToolsStatus)
  const [view, setView] = useState<View>('list')
  const [editing, setEditing] = useState<Record<string, any> | null>(null)

  useEffect(() => {
    if (show) {
      dispatch(fetchCustomTools() as any)
    }
  }, [dispatch, show])

  useEffect(() => {
    if (!show) {
      setView('list')
      setEditing(null)
    }
  }, [show])

  const goToList = () => {
    setView('list')
    setEditing(null)
  }

  const openForm = (tool: Record<string, any> | null = null) => {
    setEditing(tool)
    setView('form')
  }

  const openImport = () => setView('import')

  const handleModalDismiss = () => {
    if (view !== 'list') {
      goToList()
      return
    }
    onHide()
  }

  const handleToggleEnabled = async (tool: any) => {
    try {
      await dispatch(updateCustomTool({ toolId: tool.id, body: { is_enabled: !tool.is_enabled } }) as any).unwrap()
      await dispatch(fetchCustomTools() as any)
    } catch (e: any) {
      toast.error(e?.error || 'Update failed')
    }
  }

  const handleDelete = async (tool: any) => {
    if (!window.confirm(`Delete custom tool "${tool.name}"?`)) return
    try {
      await dispatch(deleteCustomTool(tool.id) as any).unwrap()
      toast.success('Tool deleted')
      await dispatch(fetchCustomTools() as any)
    } catch (e: any) {
      toast.error(e?.error || 'Delete failed')
    }
  }

  const handleTest = async (tool: any) => {
    try {
      const res = await dispatch(testCustomTool({ toolId: tool.id, args: {} }) as any).unwrap()
      if (res.ok) toast.success(`Test OK — HTTP ${res.status_code}`)
      else toast.error(res.error || `HTTP ${res.status_code}`)
    } catch (e: any) {
      toast.error(e?.error || 'Test failed')
    }
  }

  const headerTitle =
    view === 'list' ? (
      <span className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-inline-flex tw-rounded-lg tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-p-1.5">
          <Wrench className="tw-w-4 tw-h-4 tw-text-white" />
        </span>
        PayHelm MCP Custom Tools
      </span>
    ) : view === 'form' ? (
      editing ? 'Edit tool' : 'New tool'
    ) : (
      <span className="tw-flex tw-items-center tw-gap-2">
        <FileDown className="tw-w-4 tw-h-4 tw-text-blue-600" />
        Import from OpenAPI
      </span>
    )

  return (
    <Modal show={show} onHide={handleModalDismiss} size="xl">
      <Modal.Header closeButton className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {view !== 'list' && (
          <Button variant="link" className="p-0 me-2 d-inline-flex align-items-center text-decoration-none" onClick={goToList}>
            <ChevronLeft className="tw-w-5 tw-h-5" />
            Back
          </Button>
        )}
        <Modal.Title className="tw-flex-1 tw-mb-0" key={view}>
          {headerTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-p-0" style={{ overflow: 'hidden' }}>
        <style>{`
          @keyframes ctm-slide-in {
            from { opacity: 0; transform: translateX(24px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes ctm-slide-back {
            from { opacity: 0; transform: translateX(-24px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>
        <div
          key={view}
          style={{
            animation: view === 'list'
              ? 'ctm-slide-back 250ms ease-out both'
              : 'ctm-slide-in 250ms ease-out both',
          }}
        >
          {view !== 'list' && (
            <div className="tw-px-4 tw-pt-3 tw-pb-2 tw-border-b tw-border-gray-100 tw-bg-slate-50/90 tw-text-xs tw-text-gray-500">
              <span className="tw-text-gray-500">PayHelm MCP Custom Tools</span>
              <span className="tw-mx-1.5 tw-text-gray-300" aria-hidden>
                /
              </span>
              <span className="tw-font-medium tw-text-gray-800">
                {view === 'form' ? (editing ? 'Edit tool' : 'New tool') : 'Import from OpenAPI'}
              </span>
            </div>
          )}

        {view === 'list' && (
          <>
            <div className="tw-shrink-0 tw-px-4 tw-pt-2 tw-pb-4 tw-border-b tw-border-gray-100">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-3 tw-mx-0">
                Custom HTTP endpoints your AI agent can call in Agent mode.
              </p>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <button
                  type="button"
                  onClick={() => openForm(null)}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors"
                >
                  <Plus className="tw-w-4 tw-h-4" />
                  Add tool
                </button>
                <button
                  type="button"
                  onClick={openImport}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-lg tw-bg-white tw-text-gray-700 tw-text-sm tw-font-medium tw-border tw-border-gray-200 tw-cursor-pointer tw-shadow-sm hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <FileDown className="tw-w-4 tw-h-4" />
                  Import from OpenAPI
                </button>
              </div>
            </div>

            <div className="tw-px-4 tw-py-4 tw-max-h-[min(60vh,520px)] tw-overflow-y-auto">
              {status === 'loading' && items.length === 0 ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                  <Loader2 className="tw-w-6 tw-h-6 tw-text-blue-500 tw-animate-spin" />
                  <p className="tw-text-sm tw-text-gray-400 tw-mt-3">Loading tools...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-gray-200 tw-bg-gray-50/50 tw-py-12 tw-px-6 tw-text-center">
                  <div className="tw-mx-auto tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-4">
                    <Zap className="tw-w-6 tw-h-6 tw-text-gray-400" />
                  </div>
                  <p className="tw-text-gray-900 tw-font-medium tw-mb-1">No tools yet</p>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-5 tw-max-w-sm tw-mx-auto">
                    Add an API endpoint or import from an OpenAPI spec to get started.
                  </p>
                  <button
                    type="button"
                    onClick={() => openForm(null)}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors"
                  >
                    <Plus className="tw-w-4 tw-h-4" />
                    Add your first tool
                  </button>
                </div>
              ) : (
                <div className="tw-space-y-2">
                  {items.map((tool: any) => {
                    const methodClass =
                      customToolHttpMethodColors[tool.http_method?.toUpperCase()] || customToolHttpMethodColors.GET
                    return (
                      <div
                        key={tool.id}
                        className={`tw-group tw-rounded-xl tw-border tw-bg-white tw-px-4 tw-py-3.5 tw-transition-all hover:tw-shadow-sm ${
                          tool.is_enabled ? 'tw-border-gray-200 hover:tw-border-gray-300' : 'tw-border-gray-100 tw-opacity-60'
                        }`}
                      >
                        <div className="tw-flex tw-items-start tw-gap-3">
                          <div className="tw-hidden sm:tw-flex tw-mt-0.5 tw-rounded-lg tw-bg-gray-50 tw-p-2 tw-flex-shrink-0">
                            <Globe className="tw-w-4 tw-h-4 tw-text-gray-400" />
                          </div>
                          <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                              <span className="tw-font-medium tw-text-gray-900 tw-text-sm">{tool.name}</span>
                              <span
                                className={`tw-inline-flex tw-items-center tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-ring-1 tw-ring-inset ${methodClass}`}
                              >
                                {tool.http_method?.toUpperCase()}
                              </span>
                            </div>
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-truncate tw-font-mono">{tool.endpoint_url}</p>
                            {tool.description && (
                              <p className="tw-text-sm tw-text-gray-500 tw-mt-1.5 tw-mb-0 tw-line-clamp-2">{tool.description}</p>
                            )}
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
                            <CustomToolToggle checked={!!tool.is_enabled} onChange={() => handleToggleEnabled(tool)} />
                            <CustomToolRowMenuPortal
                              onEdit={() => openForm(tool)}
                              onTest={() => handleTest(tool)}
                              onDelete={() => handleDelete(tool)}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {view === 'form' && (
          <div className="tw-max-h-[min(75vh,640px)] tw-overflow-y-auto">
            <CustomToolForm onClose={goToList} editing={editing} hideChrome />
          </div>
        )}

        {view === 'import' && (
          <div className="tw-max-h-[min(80vh,720px)] tw-overflow-y-auto">
            <ImportSpecModal onClose={goToList} hideChrome />
          </div>
        )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default CustomToolsIntegrationsModal
